<template>
  <manual-page
    title="Solicitudes de Cotización"
    description="Gestiona las solicitudes de nuevos clientes y crea cotizaciones personalizadas."
    :show-video="true"
    :related-links="relatedLinks"
    :action-route="{ name: 'quoteRequestsIndex', query: { status: 'pending' } }"
    action-text="Ver solicitudes pendientes"
  >
    <!-- Overview -->
    <h2>¿Qué es una Solicitud de Cotización?</h2>
    <p>
      Cuando un cliente nuevo completa el cuestionario en la app, se genera una solicitud
      de cotización. Esta contiene toda la información necesaria para crear un plan de
      alimentación personalizado.
    </p>

    <div class="info-box">
      <p class="font-medium text-gray-700 mb-2">Una solicitud incluye:</p>
      <ul class="list-disc list-inside text-gray-600 space-y-1">
        <li>Datos del cliente (nombre, contacto, ubicación)</li>
        <li>Información de la mascota (especie, raza, edad, peso)</li>
        <li>Respuestas del cuestionario de salud</li>
        <li>Condiciones médicas y alergias</li>
        <li>Preferencias alimenticias</li>
      </ul>
    </div>

    <!-- List -->
    <h2>Lista de Solicitudes</h2>
    <p>La lista muestra todas las solicitudes con su estado actual:</p>
    <ul>
      <li><strong>Pendiente:</strong> Nueva, esperando ser procesada</li>
      <li><strong>Cotizada:</strong> Ya se creó una cotización</li>
      <li><strong>Rechazada:</strong> No se puede atender</li>
    </ul>

    <div class="tip-box">
      <p class="text-sm text-blue-700">
        <strong>Tip:</strong> Las solicitudes pendientes se ordenan por antigüedad.
        Intenta responder en menos de 24 horas para una buena experiencia del cliente.
      </p>
    </div>

    <!-- Review -->
    <h2>Revisar una Solicitud</h2>
    <p>Antes de cotizar, revisa cuidadosamente:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Información de la Mascota</h3>
          <p class="text-gray-600">
            Verifica peso, edad, raza y condición corporal. Estos datos determinan
            las porciones de alimento.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Condiciones de Salud</h3>
          <p class="text-gray-600">
            Revisa alergias, condiciones médicas y medicamentos. Algunos ingredientes
            pueden estar contraindicados.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Cobertura de Entrega</h3>
          <p class="text-gray-600">
            Confirma que la dirección del cliente está dentro de la zona de cobertura
            de alguna sucursal.
          </p>
        </div>
      </div>
    </div>

    <!-- Create Quote -->
    <h2>Crear Cotización</h2>
    <p>Una vez revisada la solicitud:</p>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">1</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Seleccionar Tipo de Alimento</h3>
          <p class="text-gray-600">
            Elige la plantilla de dieta más adecuada según las necesidades de la mascota.
            El sistema calcula automáticamente las porciones según el peso.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">2</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Configurar Frecuencia</h3>
          <p class="text-gray-600">
            Define cada cuántas semanas se entregará el alimento (1, 2, 3 o 4 semanas).
            Esto afecta el precio por ciclo.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">3</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Revisar Precio</h3>
          <p class="text-gray-600">
            Verifica que el precio calculado sea correcto. Puedes agregar notas
            o recomendaciones para el cliente.
          </p>
        </div>
      </div>
    </div>

    <div class="step-card">
      <div class="flex items-start">
        <span class="step-number">4</span>
        <div>
          <h3 class="font-semibold text-gray-800 mb-2">Enviar al Cliente</h3>
          <p class="text-gray-600">
            Al guardar, la cotización se envía automáticamente al cliente por email
            y notificación en la app. El cliente puede aceptarla o pedir ajustes.
          </p>
        </div>
      </div>
    </div>

    <!-- Reject -->
    <h2>Rechazar Solicitud</h2>
    <p>
      En algunos casos no es posible atender una solicitud:
    </p>
    <ul>
      <li><strong>Fuera de cobertura:</strong> No hay sucursal que pueda entregar</li>
      <li><strong>Condiciones no atendibles:</strong> Problemas de salud que requieren dieta médica especializada</li>
      <li><strong>Información incompleta:</strong> Faltan datos importantes</li>
    </ul>

    <div class="warning-box">
      <p class="text-sm text-yellow-800">
        <strong>Importante:</strong> Al rechazar, siempre indica el motivo para que
        el cliente entienda la razón y sepa si puede volver a solicitar.
      </p>
    </div>

    <!-- Support -->
    <h2>¿Necesitas ayuda?</h2>
    <p>Si tienes dudas sobre las solicitudes:</p>
    <ul>
      <li><strong>WhatsApp:</strong> +52 811 669 1329</li>
      <li><strong>Correo:</strong> soporte@trunimal.com</li>
    </ul>
  </manual-page>
</template>

<script>
import ManualPage from "@/components/manual/ManualPage.vue";

export default {
  name: "ManualQuoteRequests",

  components: {
    ManualPage,
  },

  data() {
    return {
      relatedLinks: [
        { route: "manualPlans", label: "Gestión de planes" },
        { route: "manualClients", label: "Clientes" },
        { route: "manualPets", label: "Mascotas" },
        { route: "manualDietManagement", label: "Alimentos" },
      ],
    };
  },
};
</script>
